import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

export default function ModelForm({ modelID, updateData }) {
  const [formData, setFormData] = useState({
    name: "",
    device_brand_id: "",
    order: 0,
  });
  const [error, setError] = useState(null);

  const { data: model } = useQuery({
    queryKey: ["model", modelID],
    queryFn: async () => {
      if (!modelID) return null;
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models/${modelID}`
      );
      return response.data;
    },
    enabled: !!modelID,
    onSuccess: (data) => {
      if (data) {
        setFormData({
          name: data.name,
          device_brand_id: data.device_brand_id,
          order: data.order || 0,
        });
      }
    },
    onError: () => {
      toast.error("Failed to fetch model");
    },
  });

  const { data: brands = [], isError: isBrandsError } = useQuery({
    queryKey: ["deviceBrands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT + "/device-brands?type=all"
      );
      return response.data.data || [];
    },
    onError: () => {
      setError("Failed to fetch devices. Please try again.");
      toast.error("Failed to fetch device brands");
    },
  });

  const modelMutation = useMutation({
    mutationFn: async (data) => {
      if (modelID) {
        return axiosInstance.put(
          `${window.REACT_APP_API_ENDPOINT}/device-models/${modelID}`,
          data
        );
      } else {
        return axiosInstance.post(
          window.REACT_APP_API_ENDPOINT + "/device-models",
          data
        );
      }
    },
    onSuccess: () => {
      updateData();
      setError(null);
      setFormData({
        name: "",
        device_brand_id: "",
        order: 0,
      });
      toast.success(
        modelID ? "Model updated successfully" : "Model created successfully"
      );
    },
    onError: () => {
      setError("Failed to save model. Please try again.");
      toast.error("Failed to save model");
    },
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const isAnyFieldEmpty = Object.values(formData).some(
      (value) => value === ""
    );

    if (isAnyFieldEmpty) {
      setError("Please fill in all fields.");
      toast.error("Please fill in all fields");
      return;
    }

    modelMutation.mutate(formData);
  };

  return (
    <div className=" mx-auto p-6 bg-white rounded-lg mt-4 shadow-md">
      <form className="space-y-6 max-w-xl ">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Model Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter model name"
          />
        </div>

        <div>
          <label
            htmlFor="order"
            className="block text-sm font-medium text-gray-700"
          >
            Order
          </label>
          <input
            id="order"
            name="order"
            type="number"
            required
            value={formData.order}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Brand
          </label>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(brands) &&
              brands.map((brand) => (
                <button
                  key={brand.id}
                  type="button"
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ease-in-out ${
                    formData.device_brand_id === brand.id
                      ? "bg-indigo-100 text-indigo-700 border-indigo-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } border`}
                  onClick={() => {
                    setFormData((prevData) => ({
                      ...prevData,
                      device_brand_id: brand.id,
                    }));
                  }}
                >
                  <span
                    className={`w-3 h-3 rounded-full mr-2 ${
                      formData.device_brand_id === brand.id
                        ? "bg-indigo-500"
                        : "bg-gray-300"
                    }`}
                  />
                  {brand.name}
                </button>
              ))}
            <a
              href="/admin/device-brands"
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200 ease-in-out"
            >
              <span className="w-3 h-3 rounded-full mr-2 bg-gray-300" />
              Other
            </a>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleFormSubmit}
          disabled={modelMutation.isLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            modelMutation.isLoading
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200`}
        >
          {modelMutation.isLoading ? "Saving..." : "Save Model"}
        </button>
      </form>
    </div>
  );
}
